import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import * as previousNewsletterAction from "../../../../redux/actions/previousNewsletter.action";
import { useTranslation } from "react-i18next";
import List from "../../../list/list";
import placeholder from "../../../../assets/images/placeholder.jpg";
import { fetchSASToken } from "../../../../services/SASTokenService";
import Select from "react-select";
import { getTheme } from "../../../../helpers/select.theme";
import {
  formatGroupLabel,
  getOnlyMemberAndGroupOptions,
} from "../../../../helpers/select";
import { customStyles, customStylesForTable } from "../../../../helpers/select.custom";
import RetireePortalToggle from "../../../RetireePortalToggle/RetireePortalToggle";

const previousNewsletterFields = [
  //list will sort by the first element by default
  "title",
  "active",
  "createdAt",
];

const PreviousNewsletterList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [token, setToken] = useState(null);
  const [memberOptions, setMemberOptions] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { previousNewsletters, meta, listLoading, error } = useSelector((state) => ({
    previousNewsletters: state.previousNewsletter.previousNewsletterList,
    meta: state.previousNewsletter.previousNewsletterMetadata,
    listLoading: state.previousNewsletter.listLoading,
    error: state.previousNewsletter.previousNewsletterError,
  }));

  const initialParams = {
    fieldTofilter: "title",
    filter: "",
    fieldToSort: "title",
    sortDirection: "desc",
    page: 1,
    pageSize: 10,
    memberFilters: { all: true, members: [], groups: [] },
  };

  useEffect(() => {
    previousNewsletterAction.fetchAll(dispatch, initialParams);
    const fetchToken = async () => {
      setToken(await fetchSASToken());
    };
    fetchToken();
  }, [dispatch]);

  useEffect(() => {
    if (previousNewsletters?.data && previousNewsletters?.data?.length > 0) {
      const options = previousNewsletters?.data?.reduce(
        (obj, { id, attributes: { locations, location_groups } }) => {
          obj[id] = getOnlyMemberAndGroupOptions(location_groups, locations);
          return obj;
        },
        {}
      );
      setMemberOptions(options);
    }
  }, [previousNewsletters]);

  // Handle Redux errors
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setShowAlert(false); // Don't show success alert if there's an error
    }
  }, [error]);

  // Auto-hide success alert after 5 seconds
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);


  const handleToggleRetireePortal = async (newsletterId, currentStatus) => {
    // Clear any previous messages
    setErrorMessage("");
    setAlertMessage("");
    setShowAlert(false);

    let success = false;

    try {
      // Find the newsletter data to send complete information
      const newsletterToUpdate = previousNewsletters?.data?.find(newsletter => newsletter.id === newsletterId);
      if (!newsletterToUpdate) {
        throw new Error('Newsletter not found');
      }

      const formData = new FormData();
      const { attributes } = newsletterToUpdate;

      // Exclude the file field to avoid issues with file relationships
      const { pdf, ...attributesWithoutFile } = attributes;
      const formBody = { ...attributesWithoutFile, show_in_retiree_portals: !attributes.show_in_retiree_portals }

      formData.append("data", JSON.stringify(formBody));
      await previousNewsletterAction.update(dispatch, newsletterId, formData);

      // Refresh the list to show updated data
      await previousNewsletterAction.fetchAll(dispatch, initialParams);
      
      // Mark as successful only if we reach this point
      success = true;
    } catch (error) {
      console.error('Error updating retiree portal status:', error);
      success = false;
    }

    // Show appropriate message based on success flag
    if (success) {
      const message = !currentStatus
        ? "Newsletter successfully added to retiree portals"
        : "Newsletter successfully removed from retiree portals";
      setAlertMessage(message);
      setShowAlert(true);
    } else {
      setErrorMessage("Error updating retiree portal status. Please try again.");
    }
  };

  return (
    <List
      showAlert={showAlert}
      setShowAlert={setShowAlert}
      successAlertMessage={alertMessage || "Newsletter was successfully updated."}
      customListError={errorMessage}
      sortIsDisabled={true}
      redirect="/previous-newsletter/form"
      title="Previous Medicare Newsletters"
      isOverview={false}
      tooltipTextDelete="Delete Newsletter"
      columnNames={[
        "TITLE FOR LEFT MENU",
        "PDF NAME",
        "DATE OF CREATION",
      ]}
      hasImage={false}
      entities={previousNewsletters?.data?.map(
        ({
          id,
          attributes: {
            titleForLeftMenu,
            createdAt,
            pdf,
            order,
          },
        }) => ({
          order,
          id,
          columns: [
            { primary: titleForLeftMenu },
            { primary: pdf?.data?.attributes?.name || "No file" },
            { primary: createdAt },
          ],
          name: titleForLeftMenu,
        })
      )}
      fieldsToSort={previousNewsletterFields}
      fetchAction={(params) => previousNewsletterAction.fetchAll(dispatch, {...initialParams, ...params})}
      metadata={meta}
      deleteAction={async (id, params) => {
        try {
          await previousNewsletterAction.remove(dispatch, id, params);
          // Show success alert after successful deletion
          setAlertMessage("Newsletter successfully deleted.");
          setShowAlert(true);
          setErrorMessage("");
        } catch (error) {
          // Error will be handled by the Redux error state
          console.error("Delete failed:", error);
        }
      }}
      loading={listLoading}
    />
  );
};

export default PreviousNewsletterList;